import { openAnchoredSurface } from '@kerfjs/ui/anchored-surface';
import { Chip } from '@kerfjs/ui/chip';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { placeSearchFieldCaret, readSearchField, SearchField as KerfSearchField } from '@kerfjs/ui/search-field';
import { Text } from '@kerfjs/ui/text';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import { wireSearchFields } from '@kerfjs/ui/wire-search-fields';
import { delegate, type SafeHtml } from 'kerfjs';
import { CircleHelp } from 'lucide';

import { primaryView } from '../../../components/ui-helpers.js';
import {
  currencyPickerFilter,
  searchExpanded,
  settingsPage,
  shellSearch,
  shellSearchTokens,
  view,
} from '../../../data/state.js';
import { canAssignDemands } from '../../../lib/context.js';
import {
  addShellSearchToken,
  consumeShellSearchTokens,
  mergeShellSearchTokens,
  type ShellSearchCaret,
  shellSearchTokenFromRaw,
} from '../../../lib/shell-search.js';
import { updateLibrarySearch } from '../../library/library-search.js';

export const searchSyntaxHelp = ({ chat }: { chat: boolean }): SafeHtml => {
  // Syntax chips for the help popover: one ValueTable row per filter family. Each chip is a
  // Kerf action chip; choosing one adds its filter to the search (HS2-FMRWC2).
  const chips = (values: readonly string[]): SafeHtml => (
    <Row gap="2xs" wrap>
      {values.map((value) => (
        <Chip
          tone="neutral"
          shape="rounded"
          size="compact"
          action="search-help-insert"
          itemId={value}
          actionLabel={`Add filter ${value}`}
        >
          {value}
        </Chip>
      ))}
    </Row>
  );
  const help = chat ? (
    <>
      <ValueTableRow label="State" value={chips(['is:open', 'is:closed'])} />
      <ValueTableRow label="Project" value={chips(['project:Expansion', 'project:"Plant upgrade"'])} />
    </>
  ) : (
    <>
      <ValueTableRow
        label="State"
        value={chips([
          'is:draft',
          'is:pending-review',
          'is:needs-input',
          'is:getting-quotes',
          'is:ready-to-decide',
          'is:decided',
          'is:complete',
          'is:cancelled',
        ])}
      />
      {/* The demands assigned to the signed-in staff member (FR-5.20, HS2-C63T9N). */}
      {canAssignDemands() ? <ValueTableRow label="Assigned to Me" value={chips(['is:mine'])} /> : null}
      <ValueTableRow label="Other" value={chips(['site:Karachi', 'site:"Plant One"', 'has:questions'])} />
    </>
  );
  return (
    <List gap="xs">
      <Text variant="h3">Search Syntax</Text>
      <Text tone="quiet" size="compact">
        Type words, then add any filters you need. Finish a filter with Space to turn it into a chip, or choose one
        below.
      </Text>
      <ValueTable label="Search Filters">{help}</ValueTable>
      <Text tone="quiet" size="compact">
        Words and filter chips are combined. Remove a chip with its × button, Backspace, or Delete.
      </Text>
    </List>
  );
};

/** AppShell's collapsible sidebar search, exported for an exact UX catalog specimen. */
export function SearchField({ view: v }: { view: string }): SafeHtml {
  const p = primaryView(v);
  const settingsSearch =
    p === 'stats' || v === 'library' || v === 'vendors' || (v === 'settings' && settingsPage.value === 'members');
  const searchable = p === 'demands' || p === 'chat' || settingsSearch;
  const expanded =
    searchable &&
    (searchExpanded.value || shellSearch.value !== '' || (!settingsSearch && shellSearchTokens.value.length > 0));
  const searchLabel =
    v === 'library'
      ? 'Search Knowledge Base'
      : p === 'stats'
        ? 'Search savings'
        : v === 'vendors'
          ? 'Search vendors'
          : settingsSearch
            ? 'Search members'
            : p === 'chat'
              ? 'Search chats'
              : 'Search demands';
  return (
    <KerfSearchField
      id="shell-search"
      label={searchLabel}
      query={shellSearch.value}
      tokens={(settingsSearch ? [] : shellSearchTokens.value).map((token) => ({
        value: token.raw,
        label: token.label,
        offset: token.offset,
        accessibleLabel: token.label,
      }))}
      collapsible={true}
      // No `fill`: an expanded field that consumes the trailing zone's width cannot share a row
      // with that zone's other controls, so Savings, Knowledge Base, Vendors, and Members wrapped
      // their search and profile onto extra toolbar rows. The grow group's basis keeps an editable
      // width, as on Demands and Chat (HS2-XQCWR1).
      disabled={!searchable}
      expanded={expanded}
      presentation="toolbar-group"
      placeholder={searchLabel}
      tokenPlaceholder="Add words or filters…"
      expandLabel="Open Search"
      expandAction="search-expand"
      editAction="search-token-edit"
      removeAction="search-token-remove"
      clearAction="search-clear"
      clearLabel="Clear Search"
      className="search-field"
      // Kerf owns the help button's chrome and full-slot hit target; the
      // collapsed field never renders it (HS2-X60P6V).
      trailingAction={
        settingsSearch
          ? undefined
          : {
              icon: <LucideIcon icon={CircleHelp} name="" />,
              label: 'Search Syntax Help',
              action: 'search-help',
              id: 'shell-search-help-button',
            }
      }
    />
  );
}

/**
 * Wire the toolbar search once for the page-lifetime app root: Kerf's
 * `wireSearchFields` owns the field's keyboard, collapse, and caret chrome,
 * and this app layer commits supported grammar to atomic chips and filters live.
 * It lives beside the field it wires so the wiring contract is visible to Kerf's
 * composition checks (HS2-WCD4ZG).
 */
/* eslint-disable kerfjs/prefer-attr-selector -- the app's delegated actions use data-action strings (same convention as init.ts) */
export function wireShellSearch(root: HTMLElement): () => void {
  let helpSurface: ReturnType<typeof openAnchoredSurface> | undefined;
  const closeHelp = (): void => {
    helpSurface?.close(undefined, { instant: true });
    helpSurface = undefined;
  };
  // Hot Sheet-style toolbar search: the Kerf field owns editing/keyboard chrome;
  // this app layer commits supported grammar to atomic chips and filters live.
  const editorSelector = '[data-search-field-editor="shell-search"]';
  const focusShellSearch = (offset?: number): void => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const editor = root.querySelector<HTMLElement>(editorSelector);
        if (editor !== null) placeSearchFieldCaret(editor, offset);
      }),
    );
  };
  // A token change rebuilds the Kerf editor, and the old one takes the caret with it:
  // keys typed before a frame-delayed refocus land outside any editor and are lost
  // (HS2-P3GM84). Like Kerf's own clear path, hand the caret to the replacement at
  // the mutation checkpoint, before the next key event can arrive.
  let stopHandoff: (() => void) | undefined;
  const handOffCaret = (editor: HTMLElement, caret?: ShellSearchCaret): void => {
    stopHandoff?.();
    const ownsFocus = (replacement: HTMLElement): boolean => {
      const active = root.ownerDocument.activeElement;
      return active === editor || active === replacement || active === null || active === root.ownerDocument.body;
    };
    const take = (): boolean => {
      const replacement = root.querySelector<HTMLElement>(editorSelector);
      if (replacement === null || replacement === editor) return false;
      if (ownsFocus(replacement)) placeCaret(replacement, caret);
      return true;
    };
    if (take()) return;
    const observer = new MutationObserver(() => {
      if (take()) stop();
    });
    const stop = (): void => {
      observer.disconnect();
      clearTimeout(timeout);
      if (stopHandoff === stop) stopHandoff = undefined;
    };
    // The input task renders synchronously; expiring after it means a late mutation can never steal focus.
    const timeout = setTimeout(stop, 0);
    stopHandoff = stop;
    observer.observe(root, { childList: true, subtree: true });
  };
  /**
   * Kerf's caret helper resolves an offset on a chip boundary to before the chip. A caret
   * the commit left after a chip goes into the last text span covering that offset instead
   * (HS2-50303H).
   */
  const placeCaret = (editor: HTMLElement, caret?: ShellSearchCaret): void => {
    if (caret?.afterToken !== true) {
      placeSearchFieldCaret(editor, caret?.offset);
      return;
    }
    let start = 0;
    let target: { node: Node; index: number } | undefined;
    for (const span of editor.querySelectorAll<HTMLElement>('[data-search-field-text]')) {
      const source = span.textContent;
      const length = source.replaceAll('\u200b', '').length;
      const node = span.firstChild;
      if (node !== null && start <= caret.offset && caret.offset <= start + length)
        target = { node, index: source === '\u200b' ? 0 : caret.offset - start };
      start += length;
    }
    if (target === undefined) {
      placeSearchFieldCaret(editor, caret.offset);
      return;
    }
    editor.focus({ preventScroll: true });
    const range = root.ownerDocument.createRange();
    range.setStart(target.node, target.index);
    range.collapse(true);
    const selection = root.ownerDocument.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };
  /**
   * The collapsed caret's offset in the field's query, or undefined when it sits after
   * everything in the editor (the usual typing position, which keeps typing at the end).
   */
  const caretQueryOffset = (editor: HTMLElement): number | undefined => {
    const selection = root.ownerDocument.getSelection();
    if (selection === null || selection.rangeCount === 0 || !selection.isCollapsed) return undefined;
    const caret = selection.getRangeAt(0);
    if (!editor.contains(caret.startContainer)) return undefined;
    const after = root.ownerDocument.createRange();
    after.setStart(caret.startContainer, caret.startOffset);
    after.setEnd(editor, editor.childNodes.length);
    if (after.toString().replaceAll('\u200b', '').trim() === '') return undefined;
    // Read the text before the caret with Kerf's own rules (BR, nbsp, zero-width spaces).
    const before = root.ownerDocument.createRange();
    before.setStart(editor, 0);
    before.setEnd(caret.startContainer, caret.startOffset);
    const copy = root.ownerDocument.createElement('div');
    copy.append(before.cloneContents());
    return readSearchField(copy).query.length;
  };
  const updateShellSearch = (editor: HTMLElement, force: boolean): void => {
    if (
      primaryView(view.value) === 'stats' ||
      view.value === 'library' ||
      view.value === 'vendors' ||
      (view.value === 'settings' && settingsPage.value === 'members')
    ) {
      const query = readSearchField(editor, []).query;
      if (view.value === 'library') updateLibrarySearch(query);
      else shellSearch.value = query;
      shellSearchTokens.value = [];
      return;
    }
    const known = shellSearchTokens.value.map((token) => ({
      value: token.raw,
      label: token.label,
      offset: token.offset,
    }));
    const read = readSearchField(editor, known);
    const retained = read.tokens.flatMap((token) => {
      const parsed =
        shellSearchTokens.value.find((item) => item.raw === token.value) ?? shellSearchTokenFromRaw(token.value);
      return parsed === undefined ? [] : [{ ...parsed, offset: token.offset }];
    });
    // Read the caret before the commit rebuilds the editor: typing at the end keeps typing at the end.
    const caret = caretQueryOffset(editor);
    const consumed =
      caret === undefined
        ? consumeShellSearchTokens(read.query, force)
        : consumeShellSearchTokens(read.query, force, caret);
    const next = mergeShellSearchTokens(retained, consumed.tokens);
    if (consumed.tokens.length > 0) handOffCaret(editor, consumed.caret);
    shellSearch.value = consumed.text;
    if (JSON.stringify(next) !== JSON.stringify(shellSearchTokens.value)) shellSearchTokens.value = next;
  };
  const fields = wireSearchFields(root, {
    onSubmit: ({ id, editor }) => {
      if (id === 'shell-search') updateShellSearch(editor, true);
    },
    onEdit: ({ id, editor, event }) => {
      if (id === 'currency-filter') {
        currencyPickerFilter.value = readSearchField(editor).query;
      } else if (id === 'shell-search') {
        updateShellSearch(editor, event.inputType === 'insertFromPaste');
      }
    },
    collapsible: { signals: { 'shell-search': searchExpanded } },
    keyboard: {
      onRemoveToken: ({ value, editor }) => {
        handOffCaret(editor);
        shellSearchTokens.value = shellSearchTokens.value.filter((token) => token.raw !== value);
      },
    },
  });
  const off1 = delegate(root, 'click', '[data-action="search-token-remove"]', (_e, el) => {
    const value = (el as HTMLElement).dataset.tokenValue;
    if (value !== undefined) shellSearchTokens.value = shellSearchTokens.value.filter((token) => token.raw !== value);
    focusShellSearch();
  });
  const off2 = delegate(root, 'click', '[data-action="search-token-edit"]', (_e, el) => {
    const value = (el as HTMLElement).dataset.tokenValue;
    if (value === undefined) return;
    shellSearchTokens.value = shellSearchTokens.value.filter((token) => token.raw !== value);
    shellSearch.value = `${shellSearch.value}${shellSearch.value === '' ? '' : ' '}${value}`;
    focusShellSearch();
  });
  // A help chip adds its filter after typed words and returns the caret to the field.
  const onHelpInsert = (event: Event): void => {
    const el =
      event.target instanceof Element ? event.target.closest<HTMLElement>('[data-action="search-help-insert"]') : null;
    if (el === null) return;
    const next = addShellSearchToken(shellSearchTokens.value, el.dataset.itemId, shellSearch.value);
    if (next === undefined) return;
    closeHelp();
    if (next.length !== shellSearchTokens.value.length) shellSearchTokens.value = next;
    focusShellSearch();
  };
  const off5 = delegate(root, 'click', '[data-action="search-help"]', () => {
    if (helpSurface?.el.isConnected === true) {
      closeHelp();
      return;
    }
    const anchor = root.querySelector('[data-component="search-field"]:has(#shell-search-help-button)');
    if (anchor === null) return;
    const opened = openAnchoredSurface(anchor, () => searchSyntaxHelp({ chat: primaryView(view.value) === 'chat' }), {
      label: 'Search Syntax',
      align: 'end',
      initialFocus: false,
    });
    opened.el.classList.add('shell-search-help');
    opened.el.setAttribute('data-search-field-keep-open', '');
    opened.el.addEventListener('click', onHelpInsert);
    helpSurface = opened;
    void opened.result.then(() => {
      if (helpSurface === opened) helpSurface = undefined;
    });
  });
  const helpAnchorObserver = new MutationObserver(() => {
    if (helpSurface !== undefined && root.querySelector('#shell-search-help-button') === null) closeHelp();
  });
  helpAnchorObserver.observe(root, { childList: true, subtree: true });
  const off4 = delegate(root, 'click', '[data-action="search-clear"]', () => {
    closeHelp();
    shellSearch.value = '';
    shellSearchTokens.value = [];
    searchExpanded.value = false;
  });
  return () => {
    closeHelp();
    helpAnchorObserver.disconnect();
    stopHandoff?.();
    fields.dispose();
    off1();
    off2();
    off4();
    off5();
  };
}
